import { inject, Injectable, signal } from '@angular/core';
import { GetMainUserPhotosRegimenClassEnum, InstantCheckup } from '@cureskin/api-client/src/server-api';
import { ConnectionService } from '@services/connection-service';
import { ConsultationSessionService } from '@services/consultation-session-service';
import { EventLoggerService } from '@services/event-logger-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { SessionStorageService } from '@services/session-storage-service';
import { REGIMEN_CLASS_TO_INSTANT_CHECKUP_TYPES } from 'client/app/app.constants';

export type IncompleteChatClass = GetMainUserPhotosRegimenClassEnum;

type BannerPhotoByChatClass = Partial<Record<IncompleteChatClass, InstantCheckup>>;

export function pickBannerPhoto(checkups: InstantCheckup[], priority: ReadonlyArray<string>): InstantCheckup | null {
  for (const type of priority) {
    const photo = checkups.find((checkup): boolean => checkup?.type === type && !!checkup?.imagePath);
    if (photo) return photo;
  }
  return null;
}

@Injectable({ providedIn: 'root' })
export class IncompleteChatBannerService {
  private readonly conn = inject(ConnectionService);
  private readonly consultationSessionService = inject(ConsultationSessionService);
  private readonly eventLogger = inject(EventLoggerService);
  private readonly experimentService = inject(ExperimentService);
  private readonly sessionStorage = inject(SessionStorageService);

  private readonly BOTTOM_SHEET_SHOWN_KEY = 'incompleteChatBottomsheetShown';
  private readonly bannerPhotoByChatClass = signal<BannerPhotoByChatClass>({});
  private readonly faceMainConcern = signal<string | undefined>(undefined);

  async loadPhotos(pendingClasses: IncompleteChatClass[]): Promise<void> {
    if (!this.experimentService.isIncompleteChatBannerRebrandedEnabled() || !pendingClasses.length) {
      this.bannerPhotoByChatClass.set({});
      return;
    }
    const fetchedPhotos = await Promise.all(
      pendingClasses.map((regimenClass: IncompleteChatClass): Promise<InstantCheckup | null> => this.fetchBannerPhoto(regimenClass)));
    const bannerPhotos: BannerPhotoByChatClass = {};
    pendingClasses.forEach((regimenClass: IncompleteChatClass, index: number): void => {
      const photo = fetchedPhotos[index];
      if (photo) bannerPhotos[regimenClass] = photo;
    });
    if (bannerPhotos.FACE) this.faceMainConcern.set(await this.findFaceMainConcern());
    this.bannerPhotoByChatClass.set(bannerPhotos);
  }

  isRebranded(regimenClass: IncompleteChatClass): boolean {
    return !!this.bannerPhotoByChatClass()[regimenClass];
  }

  getPhoto(regimenClass: IncompleteChatClass): InstantCheckup | null {
    return this.bannerPhotoByChatClass()[regimenClass] ?? null;
  }

  getExcludeList(): string[] {
    return this.experimentService.getExcludeInstantCheckupProblems();
  }

  getFaceMainConcern(): string | undefined {
    return this.faceMainConcern();
  }

  isBottomSheetShown(): boolean {
    return this.sessionStorage.getJson<boolean>(this.BOTTOM_SHEET_SHOWN_KEY, false);
  }

  setBottomSheetShown(shown: boolean): void {
    this.sessionStorage.setJson<boolean>(this.BOTTOM_SHEET_SHOWN_KEY, shown);
  }

  private async fetchBannerPhoto(regimenClass: IncompleteChatClass): Promise<InstantCheckup | null> {
    try {
      const checkups = await this.conn.getMainUserPhotos(regimenClass);
      return pickBannerPhoto(checkups, REGIMEN_CLASS_TO_INSTANT_CHECKUP_TYPES[regimenClass]);
    } catch (error) {
      this.eventLogger.logError(error, true, 'IncompleteChatBannerService.fetchBannerPhoto');
      return null;
    }
  }

  private async findFaceMainConcern(): Promise<string | undefined> {
    const consultations = await this.consultationSessionService.getConsultations(this.conn.getActingUser());
    return this.consultationSessionService.getFaceConsultation(consultations ?? [])?.get('PrivateMainConcern');
  }
}
